import { HttpClient } from "services/HttpClient";
import { Clinician } from "types/Clinician";
import { getData } from "../contexts/authContext";
import {
  ApiPaginatedResponse,
  CommonApiParams,
  FilterDetailsParams,
} from "types/common";
import { Patient } from "types/Patient";
import { ClinicType } from "types/Clinic";
import qs from "qs";
import { RecoveryPlanType } from "../types/RecoveryPlan";
import * as cognito from "../libs/cognito";
import { capitalize } from "utils/formatters";
export type CreateClinicianPayload = {
  email: string;
  firstName: string;
  lastName: string;
  clinicId?: string;
  role: string[];
};

export type AssignAthleteToCoachPayload = {
  clinicianEmail?: string;
  patientId?: string;
};

export type UpdateClinicianPayload = {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  avatarUrl?: string;
  role?: string[];
  sports?: string[];
  inviteDates?: string[];
  authStatus?: string;
  mfaEnabled?: boolean;
};

export type CreateClinicPayload = {
  name: string;
  address: string;
  numberOfEmployees: string;
  status?: string;
};
export class ClinicRepository extends HttpClient {
  public getSelectedClinician = async (clinicianId: string) => {
    const token = await getData("idToken");
    const { data } = await this.instance.get<Clinician>(
      `/clinician/${clinicianId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  };

  public getCliniciansList = async (
    params: CommonApiParams & {
      search?: string;
      inviteDate?: string;
      authStatus?: string;
      roles?: string;
    }
  ) => {
    const token = await getData("idToken");
    const { data } = await this.instance.get<ApiPaginatedResponse<Clinician>>(
      `/clinician`,
      {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    const items = data.cliniciansList;
    const parsedData: ApiPaginatedResponse<Clinician> = {
      ...data,
      items,
    };
    return parsedData;
  };
  public getClinicianPatientStats = async (
    startDate: Date,
    endDate: Date,
    clinicId: string,
    sort: string
  ) => {
    const token = await getData("idToken");
    const { data } = await this.instance.get<any>(
      `/clinician/patient-stats-by-clinic/${clinicId}?startDate=${startDate}&endDate=${endDate}&patientSort=${sort}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  };

  public getClinicTeamLeadsList = async () => {
    const token = await getData("idToken");
    const teamLeadParams = {
      offset: 0,
      limit: 1000,
      showClinicTeamLead: true,
    };
    const { data } = await this.instance.get<ApiPaginatedResponse<Clinician>>(
      `/clinician`,
      {
        params: teamLeadParams,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    const items = data.cliniciansList;
    const parsedData: ApiPaginatedResponse<Clinician> = {
      ...data,
      items,
    };
    return parsedData;
  };

  public getClinicRecoveryTemplates = async (clinicId: string) => {
    const token = await getData("idToken");
    const { data } = await this.instance.get<any>(
      `/recovery-plan-template/${clinicId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  };

  public getClinicTeamMembersList = async () => {
    const token = await getData("idToken");
    const teamMembersParams = {
      offset: 0,
      limit: 1000,
      showClinicTeamMembers: true,
    };
    const { data } = await this.instance.get<ApiPaginatedResponse<Clinician>>(
      `/clinician`,
      {
        params: teamMembersParams,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    const items = data.cliniciansList;
    const parsedData: ApiPaginatedResponse<Clinician> = {
      ...data,
      items,
    };
    return parsedData;
  };

  public assignAthleteToCoach = async (
    payload: AssignAthleteToCoachPayload
  ) => {
    const token = await getData("idToken");
    const response = await this.instance.post(
      `/clinician/assign-athlete-to-coach`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response;
  };

  public createClinicianOnInvite = async (payload: CreateClinicianPayload) => {
    const firstNameCapitalized = capitalize(payload.firstName);
    const lastNameCapitalized = capitalize(payload.lastName);
    const parsedPayload = {
      ...payload,
      firstName: firstNameCapitalized,
      lastName: lastNameCapitalized,
    };
    const token = await getData("idToken");
    const { data } = await this.instance.post(`/clinician`, parsedPayload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return data;
  };

  public createRecoveryTemplate = async (
    payload: Omit<RecoveryPlanType, "_id">
  ) => {
    const token = await getData("idToken");

    const { data } = await this.instance.post<RecoveryPlanType>(
      `/recovery-plan-template`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return data;
  };

  public editRecoveryTemplate = async (
    payload: Omit<RecoveryPlanType, "_id">,
    id: string
  ) => {
    const token = await getData("idToken");

    const { data } = await this.instance.patch<RecoveryPlanType>(
      `/recovery-plan-template/${id}`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return data;
  };

  public deleteClinician = async (id: string) => {
    const token = await getData("idToken");

    const { data } = await this.instance.delete(`/clinician/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return data;
  };

  public deleteClinicianFromCognito = async (id: string) => {
    const token = await getData("idToken");

    const { data } = await this.instance.delete(
      `/clinician/${id}/cognito-delete`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  };

  public disableClinicianFromCognito = async (id: string) => {
    const token = await getData("idToken");

    const { data } = await this.instance.delete(`/clinician/${id}/disable`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return data;
  };

  public deleteRecoveryTemplate = async (id: string) => {
    const token = await getData("idToken");

    const { data } = await this.instance.delete(
      `/recovery-plan-template/${id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  };

  public enableClinicianFromCognito = async (id: string) => {
    const token = await getData("idToken");

    const { data } = await this.instance.patch(
      `/clinician/${id}/enable`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  };

  public enableClinic = async (id: string | undefined) => {
    const token = await getData("idToken");

    const { data } = await this.instance.patch(
      `/clinic/${id}/enable`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  };

  public disableClinic = async (id: string | undefined) => {
    const token = await getData("idToken");

    const { data } = await this.instance.delete(`/clinic/${id}/disable`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return data;
  };

  public removeClinicianFromCognitoGroup = async (
    id: string,
    groupName: string
  ) => {
    const token = await getData("idToken");

    const { data } = await this.instance.delete(
      `/clinician/${id}/remove-from-group?groupName=${groupName}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  };

  public updateCurrentClinician = async (
    clinicianId: string,
    payload: UpdateClinicianPayload
  ) => {
    const token = await getData("idToken");

    const { data } = await this.instance.patch(
      `/clinician/${clinicianId}`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  };

  public updateClinicianAvatar = async (formData: FormData) => {
    try {
      const token = await getData("idToken");

      const { data } = await this.instance.post("/clinician/avatar", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      return data;
    } catch (error) {
      throw error;
    }
  };

  public getClinicianPatients = async (
    clinicianId: string,
    params: CommonApiParams,
    searchingFor?: string,
    detailedFilter?: FilterDetailsParams
  ) => {
    const token = await getData("idToken");

    let requestParams: any = {
      params,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    if (searchingFor) {
      requestParams = {
        params: {
          ...params,
          search: searchingFor,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
    }

    if (detailedFilter) {
      requestParams = {
        params: {
          ...params,
          ...detailedFilter,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
        paramsSerializer: (params: any) => {
          return qs.stringify(params, { arrayFormat: "brackets" });
        },
      };
    }
    const { data } = await this.instance.get<ApiPaginatedResponse<Patient>>(
      `/clinician/${clinicianId}/patients`,
      requestParams
    );
    return data;
  };

  public createClinic = async (payload: CreateClinicPayload) => {
    try {
      const token = await getData("idToken");
      const { data } = await this.instance.post<ClinicType>(
        `/clinic`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data;
    } catch (error) {
      throw error;
    }
  };

  public updateClinic = async (
    clinicId: string,
    payload: Partial<CreateClinicPayload>
  ) => {
    try {
      const token = await getData("idToken");
      const { data } = await this.instance.patch<ClinicType>(
        `/clinic/${clinicId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data;
    } catch (error) {
      throw error;
    }
  };

  public getClinicList = async (
    params: CommonApiParams & { search?: string }
  ) => {
    const token = await getData("idToken");
    const { data } = await this.instance.get<ApiPaginatedResponse<ClinicType>>(
      `/clinic`,
      {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  };

  public getAtletesList = async (
    superAdminId: string | undefined,
    params: CommonApiParams & { search?: string }
  ) => {
    const token = await getData("idToken");
    const { data } = await this.instance.get<ApiPaginatedResponse<string>>(
      `/clinician/coaches/${superAdminId}`,
      {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  };

  public getClinicUserList = async (params: CommonApiParams) => {
    const token = await getData("idToken");
    const { data } = await this.instance.get<ApiPaginatedResponse<Clinician>>(
      `/clinic/users`,
      {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  };

  constructor() {
    super(process.env.REACT_APP_BASE_URL as string, () => {
      cognito.signOut(true);
    });
  }

  private static classInstance?: ClinicRepository;

  public static getInstance() {
    if (!this.classInstance) {
      this.classInstance = new ClinicRepository();
    }
    return this.classInstance;
  }
}
